import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  Popconfirm,
  Tag,
  Space,
  message,
  Typography,
  Image,
  Tooltip,
  Upload
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  PictureOutlined,
  NotificationOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import axiosInstance from '../services/axiosConfig';
import { BANNER_API, POPUP_API, API_BASE_URL } from '../constants/api';

const { Title, Text } = Typography;

function BannerAndPopupManagement() {
  const [activeTab, setActiveTab] = useState('banners');
  
  // Banner state
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [bannerModalVisible, setBannerModalVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerFileList, setBannerFileList] = useState([]);
  const [bannerForm] = Form.useForm();
  const [submittingBanner, setSubmittingBanner] = useState(false);

  // Popup state
  const [popups, setPopups] = useState([]);
  const [loadingPopups, setLoadingPopups] = useState(false);
  const [popupModalVisible, setPopupModalVisible] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [popupFileList, setPopupFileList] = useState([]);
  const [popupForm] = Form.useForm();
  const [submittingPopup, setSubmittingPopup] = useState(false);

  useEffect(() => {
    fetchBanners();
    fetchPopups();
  }, []);

  // Fetch Banners
  const fetchBanners = async () => {
    setLoadingBanners(true);
    try {
      const res = await axiosInstance.get(BANNER_API.LIST);
      if (res.data && res.data.success) {
        setBanners(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
      message.error('Không thể tải danh sách banner');
    } finally {
      setLoadingBanners(false);
    }
  };

  // Fetch Popups
  const fetchPopups = async () => {
    setLoadingPopups(true);
    try {
      const res = await axiosInstance.get(POPUP_API.LIST);
      if (res.data && res.data.success) {
        setPopups(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching popups:', err);
      message.error('Không thể tải danh sách popup thông báo');
    } finally {
      setLoadingPopups(false);
    }
  };

  // Helper formatting full image URL
  const formatImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = API_BASE_URL.replace(/\/api$/, '');
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // ================= BANNERS HANDLERS =================
  const handleAddBanner = () => {
    setEditingBanner(null);
    setBannerFileList([]);
    bannerForm.resetFields();
    bannerForm.setFieldsValue({
      orderIndex: 0,
      isActive: true
    });
    setBannerModalVisible(true);
  };

  const handleEditBanner = (record) => {
    setEditingBanner(record);
    if (record.imageUrl) {
      setBannerFileList([{
        uid: '-1',
        name: 'banner_image',
        status: 'done',
        url: formatImageUrl(record.imageUrl)
      }]);
    } else {
      setBannerFileList([]);
    }
    bannerForm.setFieldsValue({
      title: record.title,
      targetUrl: record.targetUrl,
      orderIndex: record.orderIndex,
      isActive: record.isActive
    });
    setBannerModalVisible(true);
  };

  const handleDeleteBanner = async (id) => {
    try {
      const res = await axiosInstance.delete(BANNER_API.DELETE(id));
      if (res.data && res.data.success) {
        message.success('Đã xóa banner');
        fetchBanners();
      }
    } catch (err) {
      message.error('Lỗi khi xóa banner');
    }
  };

  const handleToggleBannerStatus = async (record, checked) => {
    try {
      await axiosInstance.put(BANNER_API.UPDATE(record.id), {
        isActive: checked
      });
      message.success(`Đã ${checked ? 'bật' : 'tắt'} banner`);
      fetchBanners();
    } catch (err) {
      message.error('Lỗi khi thay đổi trạng thái');
    }
  };

  const handleSaveBanner = async () => {
    try {
      const values = await bannerForm.validateFields();

      if (!editingBanner && bannerFileList.length === 0) {
        message.error('Vui lòng tải lên file ảnh cho banner');
        return;
      }

      setSubmittingBanner(true);

      const formData = new FormData();
      if (values.title) formData.append('title', values.title);
      if (values.targetUrl) formData.append('targetUrl', values.targetUrl);
      if (values.orderIndex !== undefined) formData.append('orderIndex', values.orderIndex);
      formData.append('isActive', values.isActive);

      const rawFile = bannerFileList.length > 0 ? (bannerFileList[0].originFileObj || bannerFileList[0]) : null;
      if (rawFile && (rawFile instanceof File || rawFile.size)) {
        formData.append('image', rawFile);
      } else if (editingBanner?.imageUrl) {
        formData.append('imageUrl', editingBanner.imageUrl);
      }

      if (editingBanner) {
        await axiosInstance.put(BANNER_API.UPDATE(editingBanner.id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Cập nhật banner thành công');
      } else {
        await axiosInstance.post(BANNER_API.CREATE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Tạo banner mới thành công');
      }

      setBannerModalVisible(false);
      fetchBanners();
    } catch (err) {
      if (err.errorFields) return;
      message.error('Có lỗi xảy ra khi lưu banner');
    } finally {
      setSubmittingBanner(false);
    }
  };

  // ================= POPUPS HANDLERS =================
  const handleAddPopup = () => {
    setEditingPopup(null);
    setPopupFileList([]);
    popupForm.resetFields();
    popupForm.setFieldsValue({
      buttonText: 'Xem chi tiết',
      isActive: true
    });
    setPopupModalVisible(true);
  };

  const handleEditPopup = (record) => {
    setEditingPopup(record);
    if (record.imageUrl) {
      setPopupFileList([{
        uid: '-1',
        name: 'popup_image',
        status: 'done',
        url: formatImageUrl(record.imageUrl)
      }]);
    } else {
      setPopupFileList([]);
    }
    popupForm.setFieldsValue({
      title: record.title,
      content: record.content,
      linkUrl: record.linkUrl,
      buttonText: record.buttonText || 'Xem chi tiết',
      isActive: record.isActive
    });
    setPopupModalVisible(true);
  };

  const handleDeletePopup = async (id) => {
    try {
      const res = await axiosInstance.delete(POPUP_API.DELETE(id));
      if (res.data && res.data.success) {
        message.success('Đã xóa thông báo popup');
        fetchPopups();
      }
    } catch (err) {
      message.error('Lỗi khi xóa thông báo popup');
    }
  };

  const handleTogglePopupStatus = async (record, checked) => {
    try {
      await axiosInstance.put(POPUP_API.UPDATE(record.id), {
        isActive: checked
      });
      message.success(`Đã ${checked ? 'kích hoạt' : 'tắt'} thông báo popup`);
      fetchPopups();
    } catch (err) {
      message.error('Lỗi khi thay đổi trạng thái popup');
    }
  };

  const handleSavePopup = async () => {
    try {
      const values = await popupForm.validateFields();
      setSubmittingPopup(true);

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('content', values.content);
      if (values.linkUrl) formData.append('linkUrl', values.linkUrl);
      if (values.buttonText) formData.append('buttonText', values.buttonText);
      formData.append('isActive', values.isActive);

      const rawPopupFile = popupFileList.length > 0 ? (popupFileList[0].originFileObj || popupFileList[0]) : null;
      if (rawPopupFile && (rawPopupFile instanceof File || rawPopupFile.size)) {
        formData.append('image', rawPopupFile);
      } else if (editingPopup?.imageUrl) {
        formData.append('imageUrl', editingPopup.imageUrl);
      } else {
        formData.append('imageUrl', '');
      }

      if (editingPopup) {
        await axiosInstance.put(POPUP_API.UPDATE(editingPopup.id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Cập nhật thông báo popup thành công');
      } else {
        await axiosInstance.post(POPUP_API.CREATE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Tạo thông báo popup mới thành công');
      }

      setPopupModalVisible(false);
      fetchPopups();
    } catch (err) {
      if (err.errorFields) return;
      message.error('Có lỗi xảy ra khi lưu thông báo popup');
    } finally {
      setSubmittingPopup(false);
    }
  };

  // Columns Banners
  const bannerColumns = [
    {
      title: 'STT',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 80,
      render: (val) => <Tag color="blue">{val}</Tag>
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 140,
      render: (url) => (
        <Image
          src={formatImageUrl(url)}
          alt="Banner"
          width={120}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 6 }}
          fallback="https://via.placeholder.com/120x60?text=No+Image"
        />
      )
    },
    {
      title: 'Tiêu đề / Mô tả',
      dataIndex: 'title',
      key: 'title',
      render: (text) => text ? <Text bold>{text}</Text> : <Text type="secondary">Chưa có tiêu đề</Text>
    },
    {
      title: 'Đường link chi tiết (Target URL)',
      dataIndex: 'targetUrl',
      key: 'targetUrl',
      render: (url) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <LinkOutlined style={{ marginRight: 4 }} />
          {url}
        </a>
      ) : <Text type="secondary">Không gắn link</Text>
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => handleToggleBannerStatus(record, checked)}
          checkedChildren="Bật"
          unCheckedChildren="Tắt"
        />
      )
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1890ff' }} />}
              onClick={() => handleEditBanner(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xác nhận xóa banner này?"
            onConfirm={() => handleDeleteBanner(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Columns Popups
  const popupColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70
    },
    {
      title: 'Ảnh đính kèm',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 110,
      render: (url) => url ? (
        <Image
          src={formatImageUrl(url)}
          alt="Popup"
          width={90}
          height={50}
          style={{ objectFit: 'cover', borderRadius: 6 }}
        />
      ) : <Tag>Không ảnh</Tag>
    },
    {
      title: 'Tiêu đề thông báo',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Text strong style={{ color: '#fa8c16' }}>{text}</Text>
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (text) => <Text>{text}</Text>
    },
    {
      title: 'Link nút hành động',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      render: (url, record) => url ? (
        <Space direction="vertical" size={2}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined style={{ marginRight: 4 }} />
            {url}
          </a>
          <Tag color="orange">{record.buttonText || 'Xem chi tiết'}</Tag>
        </Space>
      ) : <Text type="secondary">Chỉ hiển thị thông báo</Text>
    },
    {
      title: 'Đang hiển thị',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 130,
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => handleTogglePopupStatus(record, checked)}
          checkedChildren="Hoạt động"
          unCheckedChildren="Tắt"
        />
      )
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1890ff' }} />}
              onClick={() => handleEditPopup(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xác nhận xóa thông báo này?"
            onConfirm={() => handleDeletePopup(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            <PictureOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
            Quản Lý Banner Slider & Thông Báo Pop-up (App Mobile)
          </Title>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchBanners();
              fetchPopups();
            }}
          >
            Làm mới
          </Button>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* TAB 1: BANNERS */}
          <Tabs.TabPane
            tab={
              <span>
                <PictureOutlined />
                Banner Slider App ({banners.length})
              </span>
            }
            key="banners"
          >
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">
                Các ảnh banner sẽ chạy dạng Slider ở phần trên cùng màn hình Trang chủ App Mobile. Nhấp vào ảnh sẽ mở link tương ứng.
              </Text>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddBanner}>
                Thêm Banner Mới
              </Button>
            </div>

            <Table
              dataSource={banners}
              columns={bannerColumns}
              rowKey="id"
              loading={loadingBanners}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>

          {/* TAB 2: POPUPS */}
          <Tabs.TabPane
            tab={
              <span>
                <NotificationOutlined />
                Thông Báo Pop-up App ({popups.length})
              </span>
            }
            key="popups"
          >
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">
                Thông báo dạng Pop-up sẽ tự động nổi lên khi người dùng bật ứng dụng mobile (Có thể gắn ảnh và nút bấm chuyển hướng trình duyệt).
              </Text>
              <Button type="primary" style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }} icon={<PlusOutlined />} onClick={handleAddPopup}>
                Tạo Pop-up Mới
              </Button>
            </div>

            <Table
              dataSource={popups}
              columns={popupColumns}
              rowKey="id"
              loading={loadingPopups}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* MODAL EDIT/ADD BANNER */}
      <Modal
        title={editingBanner ? 'Chỉnh sửa Banner' : 'Thêm Banner Mới'}
        open={bannerModalVisible}
        onOk={handleSaveBanner}
        onCancel={() => setBannerModalVisible(false)}
        confirmLoading={submittingBanner}
        okText="Lưu Banner"
        cancelText="Hủy"
      >
        <Form form={bannerForm} layout="vertical">
          <Form.Item
            name="title"
            label="Tiêu đề Banner (Hiển thị mờ dưới ảnh)"
          >
            <Input placeholder="Ví dụ: Khu Công Nghiệp VFI - Ưu Đãi Độc Quyền" />
          </Form.Item>

          <Form.Item
            label="Tải ảnh Banner lên"
            required={!editingBanner}
            tooltip="Chọn file hình ảnh từ máy tính (PNG, JPG, WEBP, GIF)"
          >
            <Upload
              listType="picture-card"
              fileList={bannerFileList}
              beforeUpload={(file) => {
                const isLt50M = file.size / 1024 / 1024 < 50;
                if (!isLt50M) {
                  message.error('Dung lượng ảnh phải nhỏ hơn 50MB!');
                  return Upload.LIST_IGNORE;
                }
                setBannerFileList([file]);
                return false;
              }}
              onRemove={() => setBannerFileList([])}
              maxCount={1}
              accept="image/*"
            >
              {bannerFileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Chọn ảnh</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="targetUrl"
            label="Đường dẫn liên kết khi nhấn vào (Target URL)"
          >
            <Input placeholder="Ví dụ: https://siambrothersvn.com/tin-tuc" />
          </Form.Item>

          <Form.Item
            name="orderIndex"
            label="Thứ tự hiển thị (Nhỏ xếp trước)"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Kích hoạt banner"
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>
        </Form>
      </Modal>

      {/* MODAL EDIT/ADD POPUP */}
      <Modal
        title={editingPopup ? 'Chỉnh sửa Pop-up Thông Báo' : 'Tạo Pop-up Thông Báo Mới'}
        open={popupModalVisible}
        onOk={handleSavePopup}
        onCancel={() => setPopupModalVisible(false)}
        confirmLoading={submittingPopup}
        okText="Lưu Pop-up"
        cancelText="Hủy"
      >
        <Form form={popupForm} layout="vertical">
          <Form.Item
            name="title"
            label="Tiêu đề thông báo Pop-up"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề thông báo' }]}
          >
            <Input placeholder="Ví dụ: Thông Báo Bảo Trì Hệ Thống Điện Nước" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Nội dung chi tiết thông báo"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung thông báo' }]}
          >
            <Input.TextArea rows={4} placeholder="Nhập nội dung thông báo hiển thị cho khách thuê..." />
          </Form.Item>

          <Form.Item
            label="Ảnh đính kèm Pop-up (Nếu có)"
            tooltip="Chọn file hình ảnh hiển thị trên Pop-up thông báo"
          >
            <Upload
              listType="picture-card"
              fileList={popupFileList}
              beforeUpload={(file) => {
                const isLt50M = file.size / 1024 / 1024 < 50;
                if (!isLt50M) {
                  message.error('Dung lượng ảnh phải nhỏ hơn 50MB!');
                  return Upload.LIST_IGNORE;
                }
                setPopupFileList([file]);
                return false;
              }}
              onRemove={() => setPopupFileList([])}
              maxCount={1}
              accept="image/*"
            >
              {popupFileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Chọn ảnh</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="linkUrl"
            label="Đường dẫn khi bấm nút chuyển hướng (Mở trình duyệt)"
          >
            <Input placeholder="Ví dụ: https://siambrothersvn.com/thong-bao-chi-tiet" />
          </Form.Item>

          <Form.Item
            name="buttonText"
            label="Tên nút bấm chuyển hướng"
          >
            <Input placeholder="Mặc định: Xem chi tiết" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Bật hiển thị thông báo này ngay trên App"
            valuePropName="checked"
            extra="Khi bật kích hoạt, thông báo này sẽ tự động hiện lên khi người dùng mở App."
          >
            <Switch checkedChildren="Kích hoạt" unCheckedChildren="Tắt" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BannerAndPopupManagement;
